import React from 'react';
import { motion } from 'framer-motion';
import { Award, Users, TrendingUp, Shield, Clock, Star } from 'lucide-react';

const WhyChooseUs = () => {
  const features = [
    {
      icon: Award,
      title: 'RERA Certified Experts',
      description: 'All our advisors are RERA certified professionals with deep market expertise and ethical standards.',
    },
    {
      icon: Users,
      title: 'Personalized Approach',
      description: 'Dedicated relationship managers who understand your unique requirements and lifestyle aspirations.',
    },
    {
      icon: TrendingUp,
      title: 'Priority Access',
      description: 'Exclusive pre-launch opportunities and off-market inventory from premier developers across locations.',
    },
    {
      icon: Shield,
      title: 'Discretion & Privacy',
      description: 'White-glove service with absolute confidentiality for UHNI and high-profile clientele.',
    },
    {
      icon: Clock,
      title: 'End-to-End Support',
      description: 'From site visits to legal documentation and handover—comprehensive support at every stage.',
    },
    {
      icon: Star,
      title: 'Exceptional Pricing',
      description: 'Leveraging developer relationships to secure the most competitive pricing and exclusive benefits.',
    },
  ];

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-primary text-primary-foreground" data-testid="why-choose-us-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-secondary text-sm font-medium tracking-widest uppercase">Why Venara Estate</span>
          <h2 className="font-playfair text-4xl lg:text-5xl font-bold mt-4 mb-6 tracking-tight">
            The Venara Difference
          </h2>
          <p className="text-primary-foreground/80 max-w-3xl mx-auto leading-relaxed">
            We don't just facilitate transactions—we build lasting relationships founded on trust, expertise, and
            a genuine commitment to your real estate success.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="group"
              >
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-14 h-14 border border-secondary/30 mb-6 group-hover:border-secondary transition-colors duration-500">
                  <Icon className="text-secondary" size={24} />
                </div>

                {/* Content */}
                <h3 className="font-playfair text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-primary-foreground/70 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-16"
        >
          <p className="text-primary-foreground/90 mb-6 text-lg">Ready to experience the Venara difference?</p>
          <a
            href="/contact"
            data-testid="why-choose-us-cta"
            className="inline-block bg-secondary text-secondary-foreground hover:bg-[#C5A028] px-8 py-6 font-manrope font-bold tracking-wide uppercase text-xs transition-all duration-300"
          >
            Begin Your Journey
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
